import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Stack
} from '@mui/material';
import {
  CalendarToday,
  TableChart,
  Warning,
  CheckCircle,
  AccessTime,
  PictureAsPdf,
  Download
} from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';
import { getDutyAircraftInfo } from '../ops/CrewDutyRoster/utils/crewDutyRosterUtils';
import {
  downloadAuthenticatedPdf,
  viewAuthenticatedPdf
} from '../../utils/authenticatedPdf';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { formatDateTimeWithTimezone } from '../../utils/timezoneUtils';
import CrewHoursBoard from '../ops/CrewDutyRoster/components/CrewHoursBoard';

// Use same-origin API by default in browsers; allow override via REACT_APP_API_URL for local/dev tools
const API_URL = process.env.REACT_APP_API_URL || '/api';

const CrewDutyRosterView = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [entries, setEntries] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [pdfBusyId, setPdfBusyId] = useState(null);
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });
  const [filters, setFilters] = useState({
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    status: ''
  });

  const openDutyPdf = async (entry, download = false) => {
    if (!entry?._id) return;
    setPdfBusyId(entry._id);
    try {
      const url = `${API_URL}/ops/crew-duty-roster/${entry._id}/pdf${download ? '?download=1' : ''}`;
      const day = entry.dutyStartTime
        ? new Date(entry.dutyStartTime).toISOString().slice(0, 10)
        : 'duty';
      const name = `duty-assignment-${day}.pdf`;
      if (download) await downloadAuthenticatedPdf(url, name);
      else await viewAuthenticatedPdf(url);
    } catch (error) {
      showToast(
        typeof error?.message === 'string' && !error.message.startsWith('{') && !error.message.startsWith('%PDF')
          ? error.message
          : 'Failed to open duty PDF',
        'error'
      );
    } finally {
      setPdfBusyId(null);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';

  useEffect(() => {
    fetchEntries();
    fetchCompliance();
  }, [tabValue, filters]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('crewId', user._id);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.status) params.append('status', filters.status);

      const response = await axios.get(`${API_URL}/ops/crew-duty-roster?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching duty roster entries:', error);
      showToast('Failed to fetch duty roster entries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompliance = async () => {
    try {
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/crew/${user._id}/compliance`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCompliance(response.data);
    } catch (error) {
      console.error('Error fetching compliance status:', error);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    if (displayTimezone) {
      return formatDateTimeWithTimezone(date, null, displayTimezone);
    }
    return dayjs(date).format('MMM DD, YYYY HH:mm');
  };

  const formatDuration = (hours) => {
    if (!hours) return 'N/A';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SCHEDULED': return 'info';
      case 'IN_PROGRESS': return 'warning';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>My Duty Roster</Typography>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab icon={<TableChart />} label="Table View" />
        <Tab icon={<AccessTime />} label="My Hours" />
        <Tab icon={<CalendarToday />} label="Calendar View" />
      </Tabs>

      {/* Compliance Status — shown with roster table */}
      {tabValue === 0 && compliance && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {compliance.aircraftType && (
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Aircraft Type:</strong> {compliance.aircraftType} - Compliance limits are based on {compliance.aircraftType === 'Fixed Wing' ? 'D7J-J4-Fixed Wing.pdf' : 'D7J-J2 - Helicopter.pdf'} regulations
                </Typography>
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>7-Day Flight Time</Typography>
                <Typography variant="h6">
                  {compliance.flightTime?.['7days']?.current?.toFixed(1) || 0}h / {compliance.flightTime?.['7days']?.limit || (compliance.aircraftType === 'Helicopter' ? 30 : 35)}h
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={((compliance.flightTime?.['7days']?.current || 0) / (compliance.flightTime?.['7days']?.limit || (compliance.aircraftType === 'Helicopter' ? 30 : 35))) * 100}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>28-Day Flight Time</Typography>
                <Typography variant="h6">
                  {compliance.flightTime?.['28days']?.current?.toFixed(1) || 0}h / {compliance.flightTime?.['28days']?.limit || 100}h
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={((compliance.flightTime?.['28days']?.current || 0) / (compliance.flightTime?.['28days']?.limit || 100)) * 100}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Annual Flight Time</Typography>
                <Typography variant="h6">
                  {compliance.flightTime?.annual?.current?.toFixed(1) || 0}h / {compliance.flightTime?.annual?.limit || 1000}h
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={((compliance.flightTime?.annual?.current || 0) / (compliance.flightTime?.annual?.limit || 1000)) * 100}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Weekly Rest</Typography>
                {compliance.weeklyRest?.requiresRest ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {compliance.weeklyRest.consecutiveDays} consecutive days - Rest required
                  </Alert>
                ) : (
                  <Typography variant="body2" color="success.main">
                    Compliant ({compliance.weeklyRest?.consecutiveDays || 0} consecutive days)
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Table View */}
      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Aircraft Type</TableCell>
                <TableCell>Duty Type</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Compliance</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell>PDF</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">Loading...</TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">No duty assignments found</TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => {
                  const { label: aircraftLabel } = getDutyAircraftInfo(entry);
                  return (
                  <TableRow key={entry._id}>
                    <TableCell>
                      <Box>
                        <Chip
                          label={entry.aircraftType || 'Helicopter'}
                          color={entry.aircraftType === 'Fixed Wing' ? 'primary' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                        {aircraftLabel ? (
                          <Typography component="span" variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                            {aircraftLabel}
                          </Typography>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell>{entry.dutyType}</TableCell>
                    <TableCell>{formatDateTime(entry.dutyStartTime)}</TableCell>
                    <TableCell>{formatDateTime(entry.dutyEndTime)}</TableCell>
                    <TableCell>{formatDuration(entry.dutyDuration)}</TableCell>
                    <TableCell>
                      <Chip
                        label={entry.status}
                        color={getStatusColor(entry.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {entry.isNonCompliant ? (
                        <Tooltip 
                          title={
                            <Box>
                              <Typography variant="caption" display="block">
                                Aircraft Type: {entry.aircraftType || 'Helicopter'}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {entry.nonComplianceJustification || 'Non-compliant'}
                              </Typography>
                            </Box>
                          }
                        >
                          <Chip
                            icon={<Warning />}
                            label="Non-Compliant"
                            color="error"
                            size="small"
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title={`Aircraft Type: ${entry.aircraftType || 'Helicopter'}`}>
                          <Chip
                            icon={<CheckCircle />}
                            label="Compliant"
                            color="success"
                            size="small"
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{entry.remarks || '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.25}>
                        <Tooltip title="View PDF">
                          <span>
                            <IconButton
                              size="small"
                              disabled={pdfBusyId === entry._id}
                              onClick={() => openDutyPdf(entry, false)}
                            >
                              <PictureAsPdf fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <span>
                            <IconButton
                              size="small"
                              disabled={pdfBusyId === entry._id}
                              onClick={() => openDutyPdf(entry, true)}
                            >
                              <Download fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* My Hours / FDTL */}
      {tabValue === 1 && user?._id && (
        <CrewHoursBoard
          selfCrewId={user._id}
          displayTimezone={displayTimezone}
        />
      )}

      {/* Calendar View */}
      {tabValue === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Calendar View</Typography>
          <Typography color="text.secondary">
            Calendar view implementation coming soon. Please use Table View for now.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default CrewDutyRosterView;

